import {
    AdminPanelSettings,
    AutoGraph,
    Login,
    NotificationsActive,
    Quiz,
    VideoLibrary,
} from "@mui/icons-material";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Typography,
} from "@mui/material";
import React from "react";
import {
    CHANGE_LOG_CATEGORIES,
    EFFECTIVENESS_CHANGE_LOG,
    type ChangeLogCategory,
} from "src/content/effectivenessChangeLog";
import { Reveal } from "src/theme/animation";

const CATEGORY_ICON: Record<ChangeLogCategory, React.ElementType> = {
    assessment: Quiz,
    sessions: VideoLibrary,
    access: Login,
    reminders: NotificationsActive,
    organization: AdminPanelSettings,
    measurement: AutoGraph,
};

const CATEGORY_COLOR: Record<ChangeLogCategory, "info" | "warning" | "primary" | "success" | "secondary" | "default"> = {
    assessment: "info",
    sessions: "warning",
    access: "primary",
    reminders: "success",
    organization: "secondary",
    measurement: "default",
};

type FilterId = ChangeLogCategory | "all";

function formatChangeDate(isoDate: string): string {
    const [year, month, day] = isoDate.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

const EffectivenessChangeLog: React.FC = () => {
    const [filter, setFilter] = React.useState<FilterId>("all");
    const entries = React.useMemo(
        () =>
            EFFECTIVENESS_CHANGE_LOG
                .filter((entry) => filter === "all" || entry.category === filter)
                .slice()
                .sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title)),
        [filter],
    );

    return (
        <Box id="measurement-changelog" data-label="Measurement change log" sx={{ mb: 2 }}>
            <Reveal variant="fadeUp">
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                    Pre → post growth is comparable when the questions, the session
                    experience, and who is in the sample stay stable. This log lists
                    product changes that can move those three things. Dates are when
                    the change shipped on the platform — auto-publish and AI coaching
                    only apply if an organization turned them on.
                </Typography>
            </Reveal>

            <Reveal variant="fadeUp">
                <Box
                    className="noprint"
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}
                    data-attr="measurement-changelog_filters"
                >
                    <Chip
                        label="All"
                        size="small"
                        color={filter === "all" ? "primary" : "default"}
                        variant={filter === "all" ? "filled" : "outlined"}
                        onClick={() => setFilter("all")}
                        data-attr="measurement-changelog_filter-all"
                    />
                    {CHANGE_LOG_CATEGORIES.map((category) => (
                        <Chip
                            key={category.id}
                            label={category.label}
                            size="small"
                            color={filter === category.id ? CATEGORY_COLOR[category.id] : "default"}
                            variant={filter === category.id ? "filled" : "outlined"}
                            onClick={() => setFilter(category.id)}
                            data-attr={`measurement-changelog_filter-${category.id}`}
                        />
                    ))}
                </Box>
            </Reveal>

            <Box sx={{ borderLeft: 2, borderColor: "divider", pl: { xs: 1.5, sm: 2 } }}>
                {entries.map((entry, index) => {
                    const Icon = CATEGORY_ICON[entry.category];
                    const categoryLabel =
                        CHANGE_LOG_CATEGORIES.find((item) => item.id === entry.category)?.label ??
                        entry.category;
                    return (
                        <Reveal key={entry.id} variant="fadeUp" delay={Math.min(index, 8) * 0.04}>
                            <Box
                                sx={{
                                    position: "relative",
                                    mb: 1.5,
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: { xs: -13, sm: -17 },
                                        top: 22,
                                        width: { xs: 10, sm: 14 },
                                        borderTop: 1,
                                        borderColor: "divider",
                                    },
                                }}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{ borderRadius: 2 }}
                                    data-attr="measurement-changelog_entry"
                                    data-custom-id={entry.id}
                                >
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                alignItems: "center",
                                                gap: 1,
                                                mb: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 800,
                                                    letterSpacing: 0.4,
                                                    color: "text.secondary",
                                                }}
                                            >
                                                {formatChangeDate(entry.date)}
                                            </Typography>
                                            <Chip
                                                icon={<Icon sx={{ fontSize: 16 }} />}
                                                label={categoryLabel}
                                                size="small"
                                                color={CATEGORY_COLOR[entry.category]}
                                                variant="outlined"
                                            />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
                                            {entry.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1 }}>
                                            {entry.summary}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            <Box component="strong" sx={{ fontWeight: 800 }}>
                                                Why it can affect measurement:{" "}
                                            </Box>
                                            <Box component="span" color="text.secondary">
                                                {entry.measurementNote}
                                            </Box>
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Reveal>
                    );
                })}
            </Box>
        </Box>
    );
};

export default EffectivenessChangeLog;
